import type { PerformanceSummaryData } from '@app-types/performance';

// Mock data for the Performance Summary screen.
// Values are taken from the Figma analysis (kiro_implementation_tasks.md §T05).
// Figma-visible values are preserved as-is; known inconsistencies are flagged
// with TODO(data-check) so they can be corrected without layout changes.

export const mockPerformanceSummary: PerformanceSummaryData = {
  enterpriseScore: {
    updateDate: '2026/1/30',
    totalScore: 10,
    maxScore: 10,
    industryAverage: 5.0,
    axisMax: 10,
    dimensions: [
      { key: 'profitability', label: '収益性', companyValue: 8.0, industryValue: 6.0, chartIndustryValue: 6.39 },
      { key: 'momentum', label: '株価モメンタム', companyValue: 7.0, industryValue: 5.0, chartIndustryValue: 5.57 },
      // v20 refresh (D2): the score table label drops the trailing 「性」 vs the radar.
      { key: 'soundness', label: '財務健全性', tableLabel: '財務健全', companyValue: 2.0, industryValue: 5.0, chartIndustryValue: 5.28 },
      { key: 'safety', label: '安全性', companyValue: 9.0, industryValue: 4.0, chartIndustryValue: 4.38 },
      { key: 'valuation', label: '割安性', companyValue: 10, industryValue: 5.0, chartIndustryValue: 5.39 },
    ],
    // Outlook copy is API-backed via GET /api/summary/stocks/analyst-forecast
    // (OUTLOOK_HEADR / OUTLOOK_TEXT). The mock keeps no company-specific text so
    // missing API data renders a null/no-data state instead of stale copy.
    outlookTitle: null,
    outlookText: null,
    // v20 refresh (D1): empty string renders the link with href="#" until product supplies a real URL.
    stockReportUrl: '',
  },

  indicators: [
    { key: 'pbr', label: 'PBR', companyValue: 34.24, industryValue: 36.38, unit: '倍', fractionDigits: 2 },
    { key: 'per', label: 'PER', companyValue: 51.19, industryValue: 30.74, unit: '倍', fractionDigits: 2 },
    { key: 'roe', label: 'ROE', companyValue: 171.42, industryValue: 63.59, unit: '%', fractionDigits: 2 },
    { key: 'roa', label: 'ROA', companyValue: 31.18, industryValue: 24.73, unit: '%', fractionDigits: 2 },
  ],

  // TODO(data-check): exact schedule dates are placeholders (format YYYY/MM/DD).
  schedule: [
    { key: 'earnings', label: '決算発表予定日', date: '2024/05/10' },
    { key: 'lastRight', label: '権利付き最終日', date: '2024/03/27' },
    { key: 'exRight', label: '権利落ち日', date: '2024/03/28' },
    { key: 'shareholders', label: '株主総会', date: '2024/06/20' },
  ],

  analystForecast: {
    updateDate: '2026/1/30',
    targetPrice: {
      current: 2821.0,
      highest: 3060.0,
      average: 2821.0,
      lowest: 2733.0,
      history: [
        { key: 'p01', value: 2821 },
        { key: 'p02', value: 2790 },
        { key: 'p03', value: 2774 },
        { key: 'p04', value: 2750, label: '2025/06' },
        { key: 'p05', value: 2786 },
        { key: 'p06', value: 2778 },
        { key: 'p07', value: 2822, label: '2025/09' },
        { key: 'p08', value: 2790 },
        { key: 'p09', value: 2776 },
        { key: 'p10', value: 2798, label: '2025/12' },
        { key: 'p11', value: 2765 },
        { key: 'p12', value: 2802 },
        { key: 'current', value: 2821 },
      ],
    },
    targetDescription:
      '過去3ヵ月間にNTTの予想をした14名のウォールストリートのアナリストによる12ヵ月後の予想株価より算出。平均目標株価は、2,821.00、予想最高値は3,060.00、予想最安値は2,733.00です。平均目標株価は、直近の[2,821.00から12.24%]の変動を示唆しています。',
    // TODO(data-check): the card header shows average target price 3,060.00,
    // while the chart treats 3,060.00 as the highest and 2,821.00 as average.
    cardAverageDisplay: 3060.0,
    divergence: { value: 23.6, percent: 12.24 },
    // TODO(data-check): consensus center says 37 analysts, but the segment
    // counts below sum to 46.
    analystCount: 37,
    sentiment: '弱気',
    segments: [
      { key: 'bull', label: '強気', count: 19 },
      { key: 'slightBull', label: 'やや強気', count: 8 },
      { key: 'neutral', label: '中立', count: 1 },
      { key: 'slightBear', label: 'やや弱気', count: 6 },
      { key: 'bear', label: '弱気', count: 12 },
    ],
    consensusTrend: [
      { label: '12ヶ月前', bull: 5, slightBull: 2, neutral: 0, slightBear: 1, bear: 9 },
      { label: '9ヶ月前', bull: 11, slightBull: 3, neutral: 1, slightBear: 0, bear: 8 },
      { label: '6ヶ月前', bull: 14, slightBull: 0, neutral: 3, slightBear: 2, bear: 9 },
      { label: '3ヶ月前', bull: 15, slightBull: 3, neutral: 0, slightBear: 0, bear: 11 },
      { label: '現在', bull: 19, slightBull: 8, neutral: 1, slightBear: 6, bear: 12 },
    ],
    ratingHistory: [
      { key: '12MA', value: 3.35 },
      { key: '11MA', value: 3.28 },
      { key: '10MA', value: 3.28 },
      { key: '9MA', value: 3.28 },
      { key: '8MA', value: 3.21 },
      { key: '7MA', value: 3.21 },
      { key: '6MA', value: 3.28 },
      { key: '5MA', value: 3.21 },
      { key: '4MA', value: 3.21 },
      { key: '3MA', value: 3.14 },
      { key: '2MA', value: 3.14 },
      { key: '1MA', value: 3.14 },
      { key: 'CURR', value: 3.14 },
    ],
  },

  progressStatus: {
    updateDate: null,
    // TODO(data-check): exact tab set; active tab is 経常利益 per Figma.
    tabs: [
      { key: 'revenue', label: '売上' },
      { key: 'operating', label: '営業利益' },
      { key: 'ordinary', label: '経常利益' },
      { key: 'net', label: '当期利益' },
    ],
    activeTabKey: 'ordinary',
    chart: {
      max: 500,
      unitSuffix: 'M',
      points: [
        {
          key: 'q1',
          label: '1Q',
          previous: 90,
          current: 42,
          latestCompanyForecast: null,
          consensusForecast: 48,
        },
        {
          key: 'q2',
          label: '2Q',
          previous: 150,
          current: 110,
          latestCompanyForecast: null,
          consensusForecast: 125,
        },
        {
          key: 'q3',
          label: '3Q',
          previous: 320,
          current: null,
          latestCompanyForecast: null,
          consensusForecast: 250,
        },
        {
          key: 'fullYear',
          // v20 refresh (§5.3): x-axis last label changed from '前期' → '4Q'.
          label: '4Q',
          previous: 440,
          current: null,
          latestCompanyForecast: 320,
          consensusForecast: 365,
        },
      ],
    },
    gauge: { quarterLabel: '2Q', percent: 35.5 },
    recent: { metricLabel: '経常利益', value: 2478127, progressPercent: 59.3 },
    operatingProfit: {
      nodeId: '3124:2695',
      columns: ['1Q', '２Q', '3Q', '通期'],
      rows: [
        {
          key: 'earningsDate',
          nodeId: '3124:2697',
          label: [{ text: '決算発表日' }],
          cells: [[{ text: '2025/8/7' }], [{ text: '2025/11/5' }], [{ text: '--' }], [{ text: '--' }]],
          height: 25,
        },
        {
          key: 'actual',
          nodeId: '3124:2698',
          label: [{ text: '2026/03 (連結）' }, { text: '前年同期比' }, { text: '今期進捗率' }],
          cells: [
            [{ text: '1,252,152' }, { text: '+10%', tone: 'positive' }, { text: '30.00%' }],
            [{ text: '113,736.00' }, { text: '+10%', tone: 'positive' }, { text: '59.30%' }],
            [{ text: '--' }, { text: '--' }, { text: '--%' }],
            [{ text: '--' }, { text: '--' }, { text: '--%' }],
          ],
          height: 59,
        },
        {
          key: 'latestCompany',
          nodeId: '3124:2699',
          label: [{ text: '最新会社予想' }, { text: '前年同期比' }],
          cells: [
            [{ text: '--' }, { text: '' }],
            [{ text: '--' }, { text: '' }],
            [{ text: '--' }, { text: '' }],
            [{ text: '4,410,000' }, { text: '(2025/05/08)' }, { text: '-10%', tone: 'negative' }],
          ],
          height: 59,
        },
        {
          key: 'initialCompany',
          nodeId: '3124:2700',
          label: [{ text: '当初会社予想' }, { text: '前年同期比' }],
          cells: [
            [{ text: '--' }, { text: '' }],
            [{ text: '--' }, { text: '' }],
            [{ text: '--' }, { text: '' }],
            [{ text: '4,410,000' }, { text: '(2025/05/08)' }, { text: '-10%' }],
          ],
          height: 59,
        },
        {
          key: 'consensus',
          nodeId: '3124:2755',
          label: [{ text: 'コンセンサス' }, { text: '前年同期比' }],
          cells: [
            [{ text: '1,145,225' }, { text: '+10%', tone: 'positive' }],
            [{ text: '2,463,250' }, { text: '+10%', tone: 'positive' }],
            [{ text: '3,723,017' }, { text: '+10%', tone: 'positive' }],
            [{ text: '4,863,291' }, { text: '+10%', tone: 'positive' }],
          ],
          height: 42,
        },
      ],
    },
    announcementDate: {
      nodeId: '3490:22021',
      columns: ['2024/8/1', '2024/11/6', '2025/2/5', '2025/2/8'],
      rows: [
        {
          key: 'actual202503',
          nodeId: '3490:22024',
          label: [{ text: '2025/03 (連結）' }, { text: '前年同期比' }],
          cells: [
            [{ text: '1,872,258' }, { text: '30.30%' }],
            [{ text: '2,732,069' }, { text: '44.20%' }],
            [{ text: '5,540,093' }, { text: '87.90%' }],
            [{ text: '6,414,590' }, { text: '103.20%' }],
          ],
          height: 42,
        },
      ],
    },
  },
};

export default mockPerformanceSummary;
